import type { QueryKey } from '@tanstack/react-query';
import { queryKeys } from '../query/query-keys.ts';

/*
 * Which queries a live event invalidates. Event types look like `<domain>.<what>` (`order.updated`,
 * `payment.reported`). An exact entry wins; then `domain.*` patterns; with no entry, the convention
 * invalidates the queries whose key starts with the domain (`['order', …]`).
 */

const exact = new Map<string, QueryKey[]>();
const patterns = new Map<string, QueryKey[]>();

function add(type: string, keys: readonly QueryKey[]): void {
  const target = type.endsWith('.*') ? patterns : exact;
  const name = type.endsWith('.*') ? type.slice(0, -2) : type;
  target.set(name, [...(target.get(name) ?? []), ...keys]);
}

/** Registers invalidations; returns a function that removes them (for tests and hot reload). */
export function registerLiveInvalidations(map: Record<string, readonly QueryKey[]>): () => void {
  const before = { exact: new Map(exact), patterns: new Map(patterns) };
  for (const [type, keys] of Object.entries(map)) add(type, keys);
  return () => {
    exact.clear();
    patterns.clear();
    before.exact.forEach((value, key) => exact.set(key, value));
    before.patterns.forEach((value, key) => patterns.set(key, value));
  };
}

export function domainOf(type: string): string {
  return type.split(/[.:]/)[0] ?? type;
}

export function invalidationsFor(type: string): QueryKey[] {
  const found = [...(exact.get(type) ?? [])];
  const domain = domainOf(type);
  for (const [prefix, keys] of patterns) {
    if (type === prefix || type.startsWith(`${prefix}.`)) found.push(...keys);
  }
  return found.length > 0 ? found : [[domain]];
}

/** What every surface needs; the apps register the rest next to their screens. */
registerLiveInvalidations({
  'web-home.*': [['web-home']],
  // Story ordering and invitations are saved as settings versions too.
  'settings.*': [queryKeys.publicConfig, queryKeys.settings.all, queryKeys.stories.settings, queryKeys.coupons.referrals, ['web-home']],
  'rate.*': [queryKeys.publicConfig, queryKeys.rates],
  'shift.*': [queryKeys.admin.session, queryKeys.team.all],
  'team.*': [queryKeys.admin.session, queryKeys.team.all],
  'branch.*': [queryKeys.business.session, queryKeys.business.branch, queryKeys.businesses.all],
  // A product saved in the admin changes the menus; sold out and quantities only the branch menu.
  'catalog.*': [queryKeys.catalog.all, queryKeys.businesses.all, queryKeys.business.menu, ['web-home']],
  'catalog.availability': [queryKeys.business.menu, ['menu', 'reserved']],
  'business.*': [queryKeys.businesses.all, queryKeys.business.branch, queryKeys.coupons.all, ['web-home']],
  'market.*': [queryKeys.market.all, ['web-home']],
  // Branches show the zone their point falls in; notices pick zones.
  'zones.*': [queryKeys.zones.all, queryKeys.businesses.all, ['web-home']],
  'stories.*': [queryKeys.stories.all, ['web-home']],
  'story.*': [queryKeys.stories.all, ['web-home']],
  'reels.*': [queryKeys.reels.all],
  'driver.*': [queryKeys.drivers.all, ['test-accounts']],
  'driver-application.*': [queryKeys.applications.all, queryKeys.drivers.all],
  'support.*': [['growth']],
  'rain.*': [['growth'], queryKeys.publicConfig],
  'business.proposal': [['growth']],
  'business.proposal-reviewed': [['growth']],
  'business.hygiene-updated': [['growth']],
  'business.hygiene-suspended': [['growth']],
  'business-health.*': [['growth'], queryKeys.alerts.all],
});

/*
 * Orders and their money. One order event touches its lists, the payments and the warehouse that
 * show it, and the counters of Inicio; a driver event, the cash and what is owed to drivers.
 */
registerLiveInvalidations({
  'order.*': [queryKeys.orders.all, queryKeys.payments.all, queryKeys.warehouse.all, queryKeys.operations.all, queryKeys.customers.all],
  'payment.*': [queryKeys.payments.all, queryKeys.orders.all, queryKeys.trips.all, queryKeys.operations.all],
  'alert.*': [queryKeys.alerts.all, queryKeys.orders.all, queryKeys.operations.all],
  'ratings.*': [['ratings'], queryKeys.stories.all],
  'comments.*': [['comments']],
  'claims.*': [queryKeys.claims.all, queryKeys.orders.all, queryKeys.operations.all, queryKeys.customers.all],
  'delivery.*': [queryKeys.orders.all, queryKeys.warehouse.all, queryKeys.operations.all],
  // A trip that moves changes Viajes and who is busy in Motorizados en vivo (chapter 13).
  'trip.*': [queryKeys.trips.all, queryKeys.operations.all],
  // A driver that moves or changes state moves its marker on the map of the open order.
  'driver.*': [queryKeys.operations.all, queryKeys.payouts.drivers, queryKeys.orders.details],
  'payout.*': [queryKeys.payouts.all],
  'store_order.*': [queryKeys.board.all, queryKeys.payouts.mine],
  // `chat.message` reaches admin:ORDERS with `{ deliveryId, orderId }`: the conversation and the detail.
  'chat.*': [queryKeys.orders.chats, queryKeys.orders.details],
});

/*
 * Compras and Gastos (A221–A224). What is owed mixes both; a sale moves the consignment sold
 * without a settlement and the suggested purchase list.
 */
registerLiveInvalidations({
  'purchases.*': [queryKeys.purchases.all],
  'expenses.*': [queryKeys.expenses.all, queryKeys.purchases.payablesAll],
  'market.*': [queryKeys.purchases.consignment, queryKeys.purchases.suggestions],
});
